import { AxiosResponse } from 'axios';

import { EMPTY_METADATA_PAYLOAD } from 'components/Metadata/helpers';
import { normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IGroupEditPayload, IPageType, TObject } from 'utils/types';

import axiosInstance from '../base';

import {
  flattenCpmMetadataPayload,
  normalizeCashierPaymentMethod,
  normalizeCashierPaymentMethodGroup,
  normalizeCashierPaymentMethodLocalization,
} from './normalizers';
import {
  CASHIER_LOCALIZATION_FILED_NAMES,
  ICashierPaymentMethod,
  ICashierPaymentMethodData,
  ICashierPaymentMethodGroup,
  ICashierPaymentMethodGroupData,
  ICashierPaymentMethodGroupPayload,
  ICashierPaymentMethodLocalization,
  ICashierPaymentMethodLocalizationPayload,
  ICashierPaymentMethodLocalizationUpdatePayload,
  IPaymentMethodPayload,
  IUpdatePaymentMethod,
} from './types';

const cashierPaymentMethodsAPI = {
  async getAllCashierPaymentMethods(
    filters: IAllPageFilters,
  ): Promise<IPageType<ICashierPaymentMethod>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('cashier-payment-methods', {
      params: normalizedFilter,
    });

    return normalizePageData<ICashierPaymentMethodData, ICashierPaymentMethod>(
      response.data,
      normalizeCashierPaymentMethod,
      JSON.stringify(filters.search),
    );
  },

  async getPaymentMethod(id: string): Promise<ICashierPaymentMethod> {
    const result = await axiosInstance.get(`cashier-payment-methods/${id}`);

    return normalizeCashierPaymentMethod(result.data);
  },

  async getPaymentMethodLocalization(
    id: string,
    fieldName: CASHIER_LOCALIZATION_FILED_NAMES,
  ): Promise<ICashierPaymentMethodLocalization[]> {
    const result = await axiosInstance.get(`cashier-payment-methods/${id}/localisation`, {
      params: { fieldName },
    });

    return result.data.map(normalizeCashierPaymentMethodLocalization);
  },

  async createPaymentMethodLocalization({
    id,
    payload,
  }: ICashierPaymentMethodLocalizationPayload): Promise<AxiosResponse> {
    const result = await axiosInstance.post(
      `cashier-payment-methods/${id}/localisation`,
      payload,
    );

    return result;
  },

  async updatePaymentMethodLocalization({
    id,
    payload,
  }: ICashierPaymentMethodLocalizationUpdatePayload): Promise<AxiosResponse> {
    const result = await axiosInstance.put(
      `cashier-payment-methods/localisation/${id}`,
      payload,
    );

    return result;
  },

  async deletePaymentMethodLocalization(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(
      `cashier-payment-methods/localisation/${id}`,
    );

    return result;
  },

  async createPaymentMethod(
    payload: IPaymentMethodPayload,
  ): Promise<ICashierPaymentMethod> {
    const { metadata, ...rest } = payload;
    const normalizedPayload = prepareFormData({
      ...rest,
      ...flattenCpmMetadataPayload(metadata ?? EMPTY_METADATA_PAYLOAD),
    });
    const result = await axiosInstance.post(`cashier-payment-methods`, normalizedPayload);

    return normalizeCashierPaymentMethod(result.data);
  },

  async deletePaymentMethod(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`cashier-payment-methods/${id}`);

    return result;
  },

  async updatePaymentMethod({
    id,
    ...payload
  }: IUpdatePaymentMethod): Promise<ICashierPaymentMethod> {
    const { metadata, ...rest } = payload;
    const normalizedPayload = prepareFormData({
      ...rest,
      ...flattenCpmMetadataPayload(metadata ?? EMPTY_METADATA_PAYLOAD),
    });

    const result = await axiosInstance.put(
      `cashier-payment-methods/${id}`,
      normalizedPayload,
    );

    return normalizeCashierPaymentMethod(result.data);
  },

  async groupEditPaymentMethod({
    filters,
    settings,
  }: IGroupEditPayload): Promise<AxiosResponse> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.patch('cashier-payment-methods', settings, {
      params: { search },
    });

    return response;
  },

  async getAllCashierPaymentMethodGroups(
    filters: IAllPageFilters,
  ): Promise<IPageType<ICashierPaymentMethodGroup>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('cashier-payment-methods/groups', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, ICashierPaymentMethodGroup>(
      response.data,
      normalizeCashierPaymentMethodGroup,
      JSON.stringify(filters.search),
    );
  },

  async getCashierPaymentMethodGroup(id: string): Promise<ICashierPaymentMethodGroup> {
    const response = await axiosInstance.get(`cashier-payment-methods/groups/${id}`);

    return normalizeCashierPaymentMethodGroup(
      response.data as ICashierPaymentMethodGroupData,
    );
  },

  async createCashierPaymentMethodGroup(
    payload: ICashierPaymentMethodGroupPayload,
  ): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.post(
      'cashier-payment-methods/groups',
      normalizedPayload,
    );

    return response;
  },

  async updateCashierPaymentMethodGroup(
    id: string,
    payload: ICashierPaymentMethodGroupPayload,
  ): Promise<AxiosResponse> {
    const normalizedPayload = prepareFormData(payload);
    const response = await axiosInstance.put(
      `cashier-payment-methods/groups/${id}`,
      normalizedPayload,
    );

    return response;
  },

  async deleteCashierPaymentMethodGroup(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`cashier-payment-methods/groups/${id}`);
    return response;
  },
};

export default cashierPaymentMethodsAPI;
